import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, CheckCircle2, Lock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * /rep-setup/:token — sales rep sets password + activates account.
 */
export default function RepSetup() {
  const { token } = useParams();
  const nav = useNavigate();
  const [state, setState] = useState('loading');
  const [rep, setRep] = useState(null);
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [err, setErr] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/api/rep-setup/${token}`);
        setRep(r.data.rep);
        setState('ready');
      } catch (e) {
        setErr(e.response?.data?.detail || 'Setup link invalid.');
        setState('error');
      }
    })();
  }, [token]);

  const submit = async () => {
    if (pw.length < 8) return setErr('Password must be at least 8 characters.');
    if (pw !== pw2) return setErr('Passwords do not match.');
    setSubmitting(true);
    setErr('');
    try {
      const r = await axios.post(`${API}/api/rep-setup/${token}`, { password: pw });
      localStorage.setItem('session_token', r.data.session_token);
      localStorage.setItem('user', JSON.stringify({ ...r.data.rep, role: 'sales_rep' }));
      toast.success('Account activated. Welcome!');
      nav('/rep-dashboard', { replace: true });
    } catch (e) {
      setErr(e.response?.data?.detail || 'Setup failed.');
    }
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-50 p-6">
      <div className="max-w-md w-full bg-white rounded-xl shadow-xl p-8" data-testid="rep-setup-page">
        {state === 'loading' && <Loader2 className="animate-spin mx-auto text-blue-600" size={40} />}
        {state === 'error' && (
          <div className="text-center">
            <div className="text-4xl">🔒</div>
            <h1 className="mt-4 text-xl font-semibold text-slate-900">Setup link invalid</h1>
            <p className="mt-2 text-sm text-slate-600">{err}</p>
            <p className="mt-4 text-xs text-slate-500">Ask your admin to reissue a new invite.</p>
          </div>
        )}
        {state === 'ready' && rep && (
          <>
            <div className="text-center mb-6">
              <Lock className="mx-auto text-blue-600 mb-2" size={32} />
              <h1 className="text-2xl font-bold text-slate-900">Welcome, {rep.name}</h1>
              <p className="text-sm text-slate-600 mt-1">Set your password to activate your MOSAIC rep portal.</p>
              {rep.territory && <p className="text-xs text-slate-500 mt-2">Territory: {rep.territory}</p>}
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs uppercase text-slate-500 tracking-wider">Email (locked)</label>
                <Input value={rep.email} disabled className="mt-1 bg-slate-50" />
              </div>
              <div>
                <label className="text-xs uppercase text-slate-500 tracking-wider">Choose a password (≥8 chars)</label>
                <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} className="mt-1" data-testid="rep-setup-pw" />
              </div>
              <div>
                <label className="text-xs uppercase text-slate-500 tracking-wider">Confirm password</label>
                <Input type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} className="mt-1" data-testid="rep-setup-pw2" />
              </div>
              {err && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded p-2">{err}</div>}
              <Button onClick={submit} disabled={submitting} className="w-full bg-blue-600 hover:bg-blue-700 text-white" data-testid="rep-setup-submit">
                {submitting ? <Loader2 className="animate-spin mr-2" size={16} /> : <CheckCircle2 className="mr-2" size={16} />}
                Activate my portal
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
